import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Toggle from '../ui/Toggle';
import { LabelWithHelp } from '../ui/FieldHelp';
import { TRAINING_SETTINGS_FIELD_HELP as HELP } from './trainingSettingsFieldHelp';
import { Btn, Field, Input, Tag, Skeleton } from '../ui/primitives';
import { ButtonSpinnerSlot } from '../ui/ButtonSpinner';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  MenuItem,
  TextField,
} from '@mui/material';
import axios from 'axios';
import imageCompression from 'browser-image-compression';

const FormRoot = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoCard = styled.div`
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.rLg};
  background: ${({ theme }) => theme.accentSoft};
  border: 1px solid ${({ theme }) => theme.accentLine};
`;

const InfoText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.5;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SectionLabel = styled.h4`
  margin: 0;
  font-family: ${({ theme }) => theme.fontHead};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.text};
`;

const FieldHint = styled.p`
  margin: -8px 0 0;
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.textSubtle};
  line-height: 1.45;
`;

const MediaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (min-width: 560px) {
    flex-direction: row;
    align-items: flex-end;
  }
`;

const UploadZone = styled.div`
  padding: 16px;
  border-radius: ${({ theme }) => theme.rLg};
  border: 1px dashed ${({ theme }) => theme.borderStrong};
  background: ${({ theme }) => theme.surface2};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ThumbnailPreview = styled.img`
  width: 120px;
  height: 72px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.rMd};
  border: 1px solid ${({ theme }) => theme.border};
`;

const StatusText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme, $error }) => ($error ? theme.danger : theme.success)};
`;

const GridField = styled(Field)`
  margin-bottom: 0;
  min-width: 0;
`;

const DateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const VisibilityRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.rLg};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface2};
`;

const VisibilityCopy = styled.div`
  min-width: 0;
`;

const VisibilityTitle = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.fsSm};
  font-weight: 650;
  color: ${({ theme }) => theme.text};
`;

const VisibilityHint = styled.span`
  display: block;
  margin-top: 2px;
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.textSubtle};
  line-height: 1.4;
`;

const SkeletonWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
`;

const InlineFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.border};
`;

const HiddenFile = styled.input`
  display: none;
`;

const FilePickBtn = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 0 13px;
  border-radius: ${({ theme }) => theme.rMd};
  font-size: 11px;
  font-weight: 650;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: ${({ theme }) => theme.surface2};
    border-color: ${({ theme }) => theme.borderStrong};
  }
`;

/**
 * Shimmer placeholder matching the settings form layout.
 */
const TrainingSettingFormSkeleton = () => (
  <SkeletonWrap aria-hidden="true">
    <Skeleton $h="72px" $r="12px" />
    <div>
      <Skeleton $w="72px" $h="12px" $mb="10px" />
      <SkeletonGrid>
        <Skeleton $h="40px" $r="8px" />
        <Skeleton $h="40px" $r="8px" />
      </SkeletonGrid>
      <Skeleton $h="40px" $r="8px" style={{ marginTop: 14 }} />
      <Skeleton $h="52px" $r="12px" style={{ marginTop: 14 }} />
    </div>
    <div>
      <Skeleton $w="88px" $h="12px" $mb="10px" />
      <Skeleton $h="96px" $r="12px" />
    </div>
  </SkeletonWrap>
);

/**
 * Training settings form — schedule, visibility, thumbnail, and media upload.
 * Reused inside the per-row settings modal and the legacy `/TrainingSetting` route.
 */
const TrainingSettingForm = ({
  trainingDetails,
  onDismiss,
  onSaved,
  formId,
  onActionStateChange,
  showInlineFooter = true,
  saveDisabled = false,
}) => {
  const isScormTraining = trainingDetails?.isScrom === 1;

  const [startDate, setStartDate] = useState(
    trainingDetails?.startDate
      ? new Date(parseInt(trainingDetails.startDate)).toISOString().substring(0, 10)
      : '',
  );
  const [completionDate, setCompletionDate] = useState(
    trainingDetails?.completionDate
      ? new Date(parseInt(trainingDetails.completionDate)).toISOString().substring(0, 10)
      : '',
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isAccountLoading, setIsAccountLoading] = useState(true);
  const apiUrl = process.env.REACT_APP_API_URL;
  const userId = localStorage.getItem('userId');
  const [updateAlertOpen, setUpdateAlertOpen] = useState(false);
  const [enabled, setEnabled] = useState(trainingDetails?.active === 1);
  const [thumbnail, setThumbnail] = useState(trainingDetails?.thumbnail || '');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoUploadLoading, setVideoUploadLoading] = useState(false);
  const [videoUploadMessage, setVideoUploadMessage] = useState('');
  const [htmlFiles, setHtmlFiles] = useState([]);
  const [selectedHtmlFile, setSelectedHtmlFile] = useState('');
  const [scormFolderName, setScormFolderName] = useState('');
  const [scormFiles, setScormFiles] = useState([]);
  const [openScormDialog, setOpenScormDialog] = useState(false);
  const [videoLength, setVideoLength] = useState(trainingDetails?.videoLength || 0);
  const [videoConfirmDialogOpen, setVideoConfirmDialogOpen] = useState(false);
  const [pendingVideoFile, setPendingVideoFile] = useState(null);
  const [userType, setUserType] = useState(null);
  const videoInputRef = useRef(null);

  const handleDateChange = (e) => {
    const { id, value } = e.target;
    if (id === 'startDate') setStartDate(value);
    if (id === 'completionDate') setCompletionDate(value);
  };

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  useEffect(() => {
    const storedUserType = localStorage.getItem('userType');
    setUserType(parseInt(storedUserType) || null);

    if (!trainingDetails) {
      setIsAccountLoading(false);
      return;
    }

    const fetchVideoUrl = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/${trainingDetails.trainingId}/video-url`, {
          headers: {
            Authorization: getBasicAuthHeader(),
          },
        });
        if (response.status === 200) {
          setVideoUrl(response.data);
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          // non-404 errors are ignored silently (matches legacy page behavior)
        }
        if (error.response?.status === 404) {
          setVideoUrl('');
        }
      }
    };

    const fetchAccountDetails = async () => {
      try {
        await axios.get(`${apiUrl}/api/accountdetails/${userId}`, {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            'Access-Control-Allow-Origin': '*',
            Authorization: getBasicAuthHeader(),
          },
        });
      } catch (error) {
        console.error('Failed to fetch account details:', error);
      } finally {
        setIsAccountLoading(false);
      }
    };

    fetchVideoUrl();
    fetchAccountDetails();
  }, [userId, trainingDetails?.trainingId, apiUrl]);

  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const options = {
          maxSizeMB: 0.03,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          fileType: 'image/jpeg',
        };
        const compressedFile = await imageCompression(file, options);
        const base64 = await toBase64(compressedFile);
        setThumbnail(base64);
        setThumbnailFile(file);
      } catch (error) {
        console.error('Failed to compress image:', error);
      }
    }
  };

  const toBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const resetVideoInput = () => {
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
    setVideoFile(null);
  };

  const getScormFolderPath = (file) => {
    const relativePath = file.webkitRelativePath || file.name;
    const parts = relativePath.split('/');
    return parts[0] || '';
  };

  const getHtmlFilesFromFolder = (files) => {
    const htmlFilesInFolder = files
      .filter((file) => /\.(html?|xhtml)$/i.test(file.name))
      .map((file) => {
        const relativePath = file.webkitRelativePath || file.name;
        return {
          name: file.name,
          relativePath,
          displayPath: relativePath,
        };
      });

    if (!htmlFilesInFolder.length) {
      return [];
    }

    const firstLevelHtml = htmlFilesInFolder.filter((file) => file.relativePath.split('/').length === 2);
    return firstLevelHtml.length ? firstLevelHtml : htmlFilesInFolder;
  };

  const normalizeFolderName = (name) => {
    return name.replace(/\s+/g, '_');
  };

  const handleScormFolderSelect = (files) => {
    const htmlFileList = getHtmlFilesFromFolder(files);
    const originalFolderName = getScormFolderPath(files[0]);
    const folderName = normalizeFolderName(originalFolderName);

    setScormFolderName(folderName);
    setScormFiles(files);
    setHtmlFiles(htmlFileList);
    setSelectedHtmlFile('');
    setVideoFile(null);
    setOpenScormDialog(false);

    if (htmlFileList.length) {
      setOpenScormDialog(true);
      setVideoUploadMessage('');
    } else {
      setVideoUploadMessage('No HTML files found in selected SCORM folder.');
    }
  };

  const uploadSelectedScormFile = async (selectedHtmlPath) => {
    if (!trainingDetails?.trainingId) {
      setVideoUploadMessage('Missing training ID for SCORM selection.');
      return false;
    }

    if (!scormFiles.length) {
      setVideoUploadMessage('No SCORM files available for upload.');
      return false;
    }

    setVideoUploadLoading(true);
    setVideoUploadMessage('Saving selected SCORM entry file...');

    try {
      const formData = new FormData();
      formData.append('trainingId', trainingDetails.trainingId);
      formData.append('folderName', scormFolderName);
      const htmlFileName = selectedHtmlPath.split('/').pop();
      formData.append('htmlFileName', htmlFileName);

      scormFiles.forEach((file) => {
        formData.append('files', file);
      });

      const response = await axios.post(`${apiUrl}/api/upload-training-folder`, formData, {
        headers: {
          Authorization: getBasicAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        setVideoUploadMessage('SCORM folder uploaded successfully.');
        setVideoUrl(response.data.videoUrl);
        return true;
      }

      setVideoUploadMessage('Could not upload SCORM folder.');
      return false;
    } catch (error) {
      console.error('SCORM folder upload failed:', error);
      setVideoUploadMessage('Failed to upload SCORM folder.');
      return false;
    } finally {
      setVideoUploadLoading(false);
    }
  };

  const handleVideoFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (isScormTraining) {
      handleScormFolderSelect(files);
      return;
    }

    const file = files[0];
    if (file.type !== 'video/mp4') {
      alert('Only MP4 video files are allowed.');
      resetVideoInput();
      return;
    }

    setVideoFile(file);
    setPendingVideoFile(file);
    setVideoConfirmDialogOpen(true);
  };

  const handleSelectHtmlFile = (event) => {
    const selected = event.target.value;
    setSelectedHtmlFile(selected);
  };

  const handleUploadSelectedHtmlFile = async () => {
    if (!selectedHtmlFile) {
      setVideoUploadMessage('Please select an HTML file before uploading.');
      return;
    }

    const success = await uploadSelectedScormFile(selectedHtmlFile);
    if (success) {
      setOpenScormDialog(false);
    }
  };

  const uploadVideoFile = async (file) => {
    if (!trainingDetails?.trainingId) {
      setVideoUploadMessage('Missing training ID for upload.');
      resetVideoInput();
      return;
    }

    if (trainingDetails?.isScrom !== 0) {
      setVideoUploadMessage('Video upload is only available for non-SCORM trainings.');
      resetVideoInput();
      return;
    }

    setVideoUploadLoading(true);
    setVideoUploadMessage('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('trainingId', trainingDetails.trainingId);

    try {
      const response = await axios.post(`${apiUrl}/api/upload-video`, formData, {
        headers: {
          Authorization: getBasicAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data?.success) {
        setVideoUploadMessage('Video uploaded successfully.');

        if (response.data?.videoUrl) {
          setVideoUrl(response.data.videoUrl);
        }
      } else {
        setVideoUploadMessage(response.data?.message || 'Upload failed.');
      }
    } catch (error) {
      console.error('Video upload failed:', error);
      setVideoUploadMessage(error?.response?.data?.message || 'Failed to upload video.');
    } finally {
      setVideoUploadLoading(false);
      resetVideoInput();
    }
  };

  const handleConfirmVideoUpload = async () => {
    setVideoConfirmDialogOpen(false);
    if (pendingVideoFile) {
      await uploadVideoFile(pendingVideoFile);
      setPendingVideoFile(null);
    }
  };

  const handleCancelVideoUpload = () => {
    setVideoConfirmDialogOpen(false);
    setPendingVideoFile(null);
    resetVideoInput();
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (saveDisabled) return;
    if (!startDate || !completionDate) {
      alert('Start Date and Completion Date are required.');
      return;
    }

    const startTimestamp = new Date(startDate).getTime().toString();
    const endTimestamp = new Date(completionDate).getTime().toString();

    if (!startTimestamp || !endTimestamp) {
      alert('Invalid date values provided.');
      return;
    }

    setIsLoading(true);

    const active = enabled ? 1 : 0;
    const body = {
      trainingId: trainingDetails.trainingId,
      startDate: startTimestamp,
      completionDate: endTimestamp,
      active,
      price: 0,
      thumbnail,
      videoUrl,
      videoLength,
    };

    try {
      const response = await axios.put(`${apiUrl}/api/traingSettings`, body, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      });

      if (response.status === 200) {
        setUpdateAlertOpen(true);
        setIsLoading(false);
      }
    } catch (error) {
      // legacy page swallowed errors silently
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseUpdateAlert = () => {
    setUpdateAlertOpen(false);
    if (onSaved) onSaved();
    if (onDismiss) onDismiss();
  };

  useEffect(() => {
    if (onActionStateChange) onActionStateChange({ isLoading });
  }, [isLoading, onActionStateChange]);

  if (isAccountLoading) {
    return <TrainingSettingFormSkeleton />;
  }

  return (
    <>
      <FormRoot id={formId} onSubmit={handleSubmit}>
        <InfoCard>
          <InfoText>
            Set the schedule, visibility, thumbnail, and media for this training program.
          </InfoText>
          <MetaRow>
            <Tag $tone="blue">
              SCORM {isScormTraining ? 'enabled' : 'disabled'}
            </Tag>
            {userType === 2 && videoUrl && (
              <Tag $tone="gray">Media attached</Tag>
            )}
          </MetaRow>
        </InfoCard>

        <Section>
          <SectionLabel>Schedule</SectionLabel>
          <DateGrid>
            <GridField>
              <label htmlFor="startDate"><LabelWithHelp help={HELP.startDate}>Start date</LabelWithHelp></label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={handleDateChange}
                required
              />
            </GridField>
            <GridField>
              <label htmlFor="completionDate"><LabelWithHelp help={HELP.completionDate}>Completion date</LabelWithHelp></label>
              <Input
                id="completionDate"
                type="date"
                value={completionDate}
                onChange={handleDateChange}
                required
              />
            </GridField>
          </DateGrid>
          <GridField>
            <label htmlFor="trainingTime"><LabelWithHelp help={HELP.trainingTime}>Training time (minutes)</LabelWithHelp></label>
            <Input
              id="trainingTime"
              type="number"
              min="0"
              value={videoLength}
              onChange={(e) => setVideoLength(e.target.value)}
            />
          </GridField>
          <VisibilityRow>
            <VisibilityCopy>
              <VisibilityTitle><LabelWithHelp help={HELP.visible}>Visible to learners</LabelWithHelp></VisibilityTitle>
              <VisibilityHint>When enabled, learners can see and access this training.</VisibilityHint>
            </VisibilityCopy>
            <Toggle checked={enabled} onChange={(v) => setEnabled(v)} />
          </VisibilityRow>
        </Section>

        {!videoUrl && (
          <Section>
            <SectionLabel>Media</SectionLabel>
            <FieldHint>
              {isScormTraining
                ? 'Upload a SCORM folder or paste a media URL.'
                : 'Upload an MP4 video or paste a media URL.'}
            </FieldHint>
            <MediaRow>
              <GridField style={{ flex: 1 }}>
                <label htmlFor="videoUrl"><LabelWithHelp help={HELP.mediaUrl}>Media URL</LabelWithHelp></label>
                <Input
                  id="videoUrl"
                  type="text"
                  value={videoUrl}
                  onChange={(e) => !videoUrl && setVideoUrl(e.target.value)}
                  readOnly={Boolean(videoUrl)}
                  placeholder={isScormTraining ? 'SCORM entry URL' : 'https://…'}
                />
              </GridField>
              <HiddenFile
                ref={videoInputRef}
                type="file"
                accept={isScormTraining ? undefined : 'video/mp4'}
                onChange={handleVideoFileChange}
                {...(isScormTraining ? { webkitdirectory: 'true', directory: 'true', multiple: true } : {})}
              />
              <Btn
                type="button"
                $variant="subtle"
                disabled={videoUploadLoading}
                onClick={() => videoInputRef.current?.click()}
              >
                {isScormTraining ? 'Upload folder' : 'Upload video'}
              </Btn>
            </MediaRow>
            {videoFile && (
              <FieldHint>Selected: {videoFile.name}</FieldHint>
            )}
            {videoUploadMessage && (
              <StatusText $error={videoUploadMessage.startsWith('Failed') || videoUploadMessage.includes('Could not')}>
                {videoUploadMessage}
              </StatusText>
            )}
          </Section>
        )}

        <Section>
          <SectionLabel><LabelWithHelp help={HELP.thumbnail}>Thumbnail</LabelWithHelp></SectionLabel>
          <UploadZone>
            <FieldHint>Recommended: landscape image, JPG or PNG.</FieldHint>
            <div>
              <HiddenFile
                id="thumbnail-upload"
                type="file"
                accept="image/*"
                onChange={handleThumbnailChange}
              />
              <FilePickBtn htmlFor="thumbnail-upload">Choose image</FilePickBtn>
            </div>
            {thumbnail && (
              <ThumbnailPreview src={thumbnail} alt="Thumbnail preview" />
            )}
          </UploadZone>
        </Section>

        {showInlineFooter && (
          <InlineFoot>
            {onDismiss && (
              <Btn type="button" $variant="ghost" onClick={onDismiss} disabled={isLoading}>
                Cancel
              </Btn>
            )}
            <Btn type="submit" $variant="subtle" disabled={isLoading}>
              {isLoading ? 'Saving…' : 'Save changes'}
            </Btn>
          </InlineFoot>
        )}
      </FormRoot>

      <Dialog open={updateAlertOpen} onClose={handleCloseUpdateAlert}>
        <DialogTitle>Course Updated</DialogTitle>
        <DialogContent>
          <DialogContentText>The training has been successfully updated.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateAlert} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openScormDialog} onClose={() => setOpenScormDialog(false)}>
        <DialogTitle>Select SCORM Entry File</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Select the HTML file that should act as the SCORM entry page.
          </DialogContentText>
          <TextField
            select
            label="Select HTML file"
            value={selectedHtmlFile}
            onChange={handleSelectHtmlFile}
            variant="outlined"
            margin="dense"
            fullWidth
            sx={{ mt: 2 }}
          >
            {htmlFiles.map((file) => (
              <MenuItem key={file.relativePath} value={file.relativePath}>
                {file.relativePath}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenScormDialog(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleUploadSelectedHtmlFile}
            color="primary"
            autoFocus
            disabled={!selectedHtmlFile || videoUploadLoading}
          >
            {videoUploadLoading ? <ButtonSpinnerSlot size={20} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={videoConfirmDialogOpen} onClose={handleCancelVideoUpload}>
        <DialogTitle>Confirm Video Upload</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to upload this video file? {videoFile && `(${videoFile.name})`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelVideoUpload} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmVideoUpload} color="primary" autoFocus>
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TrainingSettingForm;
